import type { ElevenLabsAccent, ElevenLabsLanguage } from "./lang";

type ElevenLabsVoiceBase = {
  id: string;
  name: string;
  gender: "masculine" | "feminine";
  description?: string;
  language: ElevenLabsLanguage;
  accent: ElevenLabsAccent;
};

export type ElevenLabsVoice = ElevenLabsVoiceBase & {
  provider: "elevenlabs";
  modelId: "eleven_v4_turbo";
};

const _ELEVEN_LABS_VOICE_BASES: ElevenLabsVoiceBase[] = [
  // English
  {
    id: "OZxMHsGaBmV5pjMIDIn0",
    name: "Amy",
    gender: "feminine",
    description: "Down-to-earth and conversational",
    language: "en",
    accent: null,
  },
  {
    id: "UgBBYS2sOqTuMpoF3BR0",
    name: "Mark",
    gender: "masculine",
    description:
      "A casual, young-adult speaking in a natural manner. Perfect for Conversational AI.",
    language: "en",
    accent: null,
  },
  {
    id: "TWutjvRaJqAX89preB4e",
    name: "Evan",
    gender: "masculine",
    description:
      "A warm, calm mid-low voice with a natural, conversational cadence. Thoughtful, grounded, and emotionally expressive through tone and pacing.",
    language: "en",
    accent: null,
  },
  {
    id: "21m00Tcm4TlvDq8ikWAM",
    name: "Rachel",
    gender: "feminine",
    description: "Calm and clear voice, well-suited for conversational use",
    language: "en",
    accent: null,
  },
  // British English, listed after the American voices so the global default stays American.
  {
    id: "DbwWo4rVEd5NrejHYUnm",
    name: "Jess",
    gender: "feminine",
    description: "British English",
    language: "en",
    accent: null,
  },
  {
    id: "KLON7Nwan8mJxpF2R8Yw",
    name: "Frederick",
    gender: "masculine",
    description: "British English",
    language: "en",
    accent: null,
  },
  // Arabic
  {
    id: "LXrTqFIgiubkrMkwvOUr",
    name: "Masry",
    gender: "masculine",
    description:
      "Clear and strong narrative voice, great for conversational use",
    language: "ar",
    accent: null,
  },
  {
    id: "a1KZUXKFVFDOb33I1uqr",
    name: "Salma",
    gender: "feminine",
    description:
      "Positive and warm professional voice, well-suited for conversational use",
    language: "ar",
    accent: null,
  },
  // Bulgarian
  {
    id: "gdk0ZsvfAOobfbTtnx6p",
    name: "Kosta",
    gender: "masculine",
    description: "Conversational Bulgarian male voice",
    language: "bg",
    accent: null,
  },
  {
    id: "fSxb5mPM1l5zTVVtM3Vb",
    name: "Elena",
    gender: "feminine",
    description:
      "Energetic and friendly voice, great for narration and podcasts",
    language: "bg",
    accent: null,
  },
  // Chinese
  {
    id: "MI36FIkp9wRP7cpWKPTl",
    name: "Evan Zhao",
    gender: "masculine",
    description:
      "A calm and trustworthy Mandarin male voice with smooth rhythm and warm tone — perfect for audiobooks, language content, and emotional narration. ",
    language: "zh",
    accent: "standard",
  },
  {
    id: "4VZIsMPtgggwNg7OXbPY",
    name: "James Gao",
    gender: "masculine",
    description:
      "James Gao - A middle-aged Chinese voice in a calm, friendly tone. Perfect for narrative and storytelling. ",
    language: "zh",
    accent: "standard",
  },
  {
    id: "APSIkVZudNbPAwyPoeVO",
    name: "Sage",
    gender: "feminine",
    description:
      "A warm, soothing female narrative voice. Calm, grounded, and gently intimate.",
    language: "zh",
    accent: "standard",
  },
  {
    id: "bhJUNIXWQQ94l8eI2VUf",
    name: "Amy",
    gender: "feminine",
    description:
      "Amy's voice sounds young and natural, with a relaxed and friendly tone—like she's chatting with a friend.",
    language: "zh",
    accent: "standard",
  },
  {
    id: "pTOe8BQRdydOEIgv0wFL",
    name: "LiuPing",
    gender: "masculine",
    description:
      "Hi, I’m Liu Ping, a native Mandarin Chinese speaker. My voice is clear, professional, and recorded in standard Chinese (普通话)",
    language: "zh",
    accent: "standard",
  },
  {
    id: "fQj4gJSexpu8RDE2Ii5m",
    name: "Yu",
    gender: "masculine",
    description: "Youthful and energetic voice with an engaging tone",
    language: "zh",
    accent: "standard",
  },
  {
    id: "kGjJqO6wdwRN9iJsoeIC",
    name: "Yui",
    gender: "feminine",
    description:
      "Delicate and soothing voice, well-suited for conversational use",
    language: "zh",
    accent: "standard",
  },
  // Cantonese
  {
    id: "R5E9sH7cGUEbuu7YE7K7",
    name: "Lucky Chan",
    gender: "masculine",
    description:
      "Professional, warm, and reassuring tone that feels respectful and easy to trust",
    language: "zh",
    accent: "cantonese",
  },
  {
    id: "n4xdXKggn5lFcXFYE4TA",
    name: "Chloe Chan",
    gender: "feminine",
    description: "Young, fast, and energetic",
    language: "zh",
    accent: "cantonese",
  },
  // Croatian
  {
    id: "kpx2bGcCm6NK1UJ5jxMH",
    name: "Davor",
    gender: "masculine",
    description: "Casual and conversational Croatian male voice",
    language: "hr",
    accent: null,
  },
  {
    id: "NfdaqpoTPn7nF7WBBRTv",
    name: "Ana",
    gender: "feminine",
    description:
      "Warm and expressive voice, well-suited for conversational use",
    language: "hr",
    accent: null,
  },
  // Czech
  {
    id: "uYFJyGaibp4N2VwYQshk",
    name: "Adam",
    gender: "masculine",
    description: "Velvety and conversational Czech male voice",
    language: "cs",
    accent: null,
  },
  {
    id: "bF7C2fCv7Zf30iT84wZ1",
    name: "Jana",
    gender: "feminine",
    description:
      "Warm and confident voice with clear articulation, great for conversations",
    language: "cs",
    accent: null,
  },
  // Danish
  {
    id: "UgBBYS2sOqTuMpoF3BR0",
    name: "Mark",
    gender: "masculine",
    description: "Casual and natural voice, well-suited for conversational use",
    language: "da",
    accent: null,
  },
  {
    id: "4RklGmuxoAskAbGXplXN",
    name: "Camilla",
    gender: "feminine",
    description: "Clear and calm voice, great for professional conversations",
    language: "da",
    accent: null,
  },
  // Dutch
  {
    id: "G53Wkf3yrsXvhoQsmslL",
    name: "James",
    gender: "masculine",
    description: "Deep and confident voice, well-suited for narration",
    language: "nl",
    accent: null,
  },
  {
    id: "XJa38TJgDqYhj5mYbSJA",
    name: "Bella",
    gender: "feminine",
    description: "Conversational Dutch female voice for customer support",
    language: "nl",
    accent: null,
  },
  // Finnish
  {
    id: "dlbXHgJnwobU5JdZ8F5M",
    name: "Jussi",
    gender: "masculine",
    description: "Strong Finnish male voice with a natural accent",
    language: "fi",
    accent: null,
  },
  {
    id: "RiWFFlzYFZuu4lPMig3i",
    name: "Soili",
    gender: "feminine",
    description: "Neutral and calm Finnish female voice",
    language: "fi",
    accent: null,
  },
  // French
  {
    id: "8qnuneLiGjGrT4A62CCe",
    name: "Jules",
    gender: "masculine",
    description: "Warm and natural conversational voice with smooth delivery",
    language: "fr",
    accent: null,
  },
  {
    id: "YxrwjAKoUKULGd0g8K9Y",
    name: "Lucie",
    gender: "feminine",
    description:
      "Professional and empathetic voice, great for conversational use",
    language: "fr",
    accent: null,
  },
  // German
  {
    id: "MMwckqU477oQxnAk1SgA",
    name: "Ben",
    gender: "masculine",
    description: "Calm and natural conversational voice for authentic dialogue",
    language: "de",
    accent: null,
  },
  {
    id: "NE7AIW5DoJ7lUosXV2KR",
    name: "Ela",
    gender: "feminine",
    description: "Cheerful and playful young German female voice",
    language: "de",
    accent: null,
  },
  // Greek
  {
    id: "TN3alZndDSA8GYZSOf3r",
    name: "Georgios",
    gender: "masculine",
    description:
      "Casual and reassuring voice, well-suited for conversational use",
    language: "el",
    accent: null,
  },
  {
    id: "Jv2zcgjn9Qu0uNMKJjb1",
    name: "Madlen",
    gender: "feminine",
    description: "Young female voice, great for casual conversations",
    language: "el",
    accent: null,
  },
  // Hindi
  {
    id: "zT03pEAEi0VHKciJODfn",
    name: "Raju",
    gender: "masculine",
    description: "Clear and warm voice with a natural, relatable tone",
    language: "hi",
    accent: null,
  },
  {
    id: "8FsOrsZSELg9otqX9nPu",
    name: "Reva",
    gender: "feminine",
    description: "Clear and expressive Hindi female voice",
    language: "hi",
    accent: null,
  },
  // Hungarian
  {
    id: "Bo3owz64xY2OSiTYnPat",
    name: "Lajos",
    gender: "masculine",
    description: "Natural and versatile Hungarian announcer voice",
    language: "hu",
    accent: null,
  },
  {
    id: "xjlfQQ3ynqiEyRpArrT8",
    name: "Vera",
    gender: "feminine",
    description: "Young and energetic voice, great for conversational use",
    language: "hu",
    accent: null,
  },
  // Indonesian
  {
    id: "TMvmhlKUioQA4U7LOoko",
    name: "Andi",
    gender: "masculine",
    description:
      "Clear and professional Indonesian male voice with a friendly tone",
    language: "id",
    accent: null,
  },
  {
    id: "fUesUKVrbYRcEnWoLXet",
    name: "Nesah",
    gender: "feminine",
    description: "Warm and calm voice with polite, controlled speech",
    language: "id",
    accent: null,
  },
  // Italian
  {
    id: "HhBRXe1sPBLe9D9lX1HC",
    name: "Raffaele",
    gender: "masculine",
    description: "Refined and confident Italian voice, great for conversations",
    language: "it",
    accent: null,
  },
  {
    id: "CiwzbDpaN3pQXjTgx3ML",
    name: "Aida",
    gender: "feminine",
    description: "Deep and smooth Italian female voice",
    language: "it",
    accent: null,
  },
  // Japanese
  {
    id: "NO5A3b3sSzDyJQF7MiNS",
    name: "Shohei",
    gender: "masculine",
    description: "Clear and measured Japanese male voice",
    language: "ja",
    accent: null,
  },
  {
    id: "RBnMinrYKeccY3vaUxlZ",
    name: "Sakura",
    gender: "feminine",
    description:
      "Young Japanese female voice, great for narration and podcasts",
    language: "ja",
    accent: null,
  },
  // Korean
  {
    id: "fLvpMIGwcTmxzsUF4z1U",
    name: "Harry",
    gender: "masculine",
    description: "Soft and steady Korean male voice with a warm tone",
    language: "ko",
    accent: null,
  },
  {
    id: "8jHHF8rMqMlg8if2mOUe",
    name: "Han",
    gender: "feminine",
    description:
      "Natural and casual voice with a spontaneous, conversational feel",
    language: "ko",
    accent: null,
  },
  // Malay
  {
    id: "lMSqoJeA0cBBNA9FeHAs",
    name: "Rizq",
    gender: "masculine",
    description: "Casual and friendly Malay male voice with a warm tone",
    language: "ms",
    accent: null,
  },
  {
    id: "vzENlyfJ5X29uOlVQMWY",
    name: "Aina",
    gender: "feminine",
    description: "Soft and friendly voice, great for natural conversations",
    language: "ms",
    accent: null,
  },
  // Norwegian
  {
    id: "vUmLiNBm6MDcy1NUHaVr",
    name: "Helge",
    gender: "masculine",
    description: "Natural and deep Norwegian male voice",
    language: "no",
    accent: null,
  },
  {
    id: "uNsWM1StCcpydKYOjKyu",
    name: "Mia",
    gender: "feminine",
    description: "Clear and friendly Norwegian female voice",
    language: "no",
    accent: null,
  },
  // Polish
  {
    id: "mr1ubFaLs5xVrh1EqWtc",
    name: "Kamil",
    gender: "masculine",
    description: "Expressive and friendly voice, great for conversational use",
    language: "pl",
    accent: null,
  },
  {
    id: "NOWYzprzTwfZQqU76pBX",
    name: "Aleksandra",
    gender: "feminine",
    description: "Professional and warm Polish female voice",
    language: "pl",
    accent: null,
  },
  // Portuguese
  {
    id: "gAzaYtjDCyG4vCelULMb",
    name: "João C.",
    gender: "masculine",
    description: "Male deep voice",
    language: "pt",
    accent: "european",
  },
  {
    id: "zKjRewuiqTkXNUVAMwat",
    name: "Mariza",
    gender: "feminine",
    description: " Clear, calm, warm, and friendly female voice",
    language: "pt",
    accent: "european",
  },
  // Portuguese (Brazilian)
  {
    id: "th5FCJmMnbjdtjRCPt3A",
    name: "Thiago",
    gender: "masculine",
    description:
      "A natural, conversational voice with a medium-low tone, excellent articulation, and a measured rhythm, ideal for technical and explanatory content",
    language: "pt",
    accent: "brazilian",
  },
  {
    id: "RGymW84CSmfVugnA5tvA",
    name: "Roberta",
    gender: "feminine",
    description:
      "A young and friendly Brazilian Portuguese female voice, ideal for your conversation AI, social media, and podcasts, story narration, bringing warmth, a relaxed and explanatory vibe",
    language: "pt",
    accent: "brazilian",
  },
  // Romanian
  {
    id: "b4bnZ9y3ZRH0myLzE2B5",
    name: "Robert",
    gender: "masculine",
    description: "Clear and calm Romanian male voice with a friendly tone",
    language: "ro",
    accent: null,
  },
  {
    id: "3z9q8Y7plHbvhDZehEII",
    name: "Antonia",
    gender: "feminine",
    description: "Warm and smooth Romanian female voice",
    language: "ro",
    accent: null,
  },
  // Russian
  {
    id: "Gee7HxcmprqMXKMfO6g5",
    name: "Dan",
    gender: "masculine",
    description: "Conversational Russian male voice",
    language: "ru",
    accent: null,
  },
  {
    id: "Jbte7ht1CqapnZvc4KpK",
    name: "Kari",
    gender: "feminine",
    description: "Warm and expressive Russian-speaking female voice",
    language: "ru",
    accent: null,
  },
  // Slovak
  {
    id: "5TUD5nYN251MvBggIfLu",
    name: "Alex",
    gender: "masculine",
    description: "Pleasant and warm Slovak male voice with a trustworthy tone",
    language: "sk",
    accent: null,
  },
  {
    id: "9Nd358gE1qQp0pDh8FgP",
    name: "Julia",
    gender: "feminine",
    description: "Soft and youthful Slovak female voice with a natural tone",
    language: "sk",
    accent: null,
  },
  // Spanish
  {
    id: "YExhVa4bZONzeingloMX",
    name: "Juan Carlos",
    gender: "masculine",
    description: "Warm and conversational Latin American male voice",
    language: "es",
    accent: null,
  },
  {
    id: "86V9x9hrQds83qf7zaGn",
    name: "Marcela",
    gender: "feminine",
    description:
      "Youthful and natural Colombian female voice, great for conversations",
    language: "es",
    accent: null,
  },
  // Swedish
  {
    id: "6eknYWL7D5Z4nRkDy15t",
    name: "Tommy",
    gender: "masculine",
    description: "Warm and informative Swedish male voice",
    language: "sv",
    accent: null,
  },
  {
    id: "cLAH1kXlkAivJHxCW601",
    name: "Olivia",
    gender: "feminine",
    description: "Bright and youthful Swedish female voice",
    language: "sv",
    accent: null,
  },
  // Tamil
  {
    id: "gJvkwI7wGFW2czmyfJhp",
    name: "Muthu",
    gender: "masculine",
    description: "Calm and friendly Tamil male voice, great for conversations",
    language: "ta",
    accent: null,
  },
  {
    id: "gCr8TeSJgJaeaIoV4RWH",
    name: "Meera",
    gender: "feminine",
    description: "Friendly and conversational Tamil female voice",
    language: "ta",
    accent: null,
  },
  // Tagalog / Filipino
  {
    id: "1ZA0KgPdD0Tns6SR4FVQ",
    name: "Juan",
    gender: "masculine",
    description: "Clear and expressive Filipino male voice with a warm tone",
    language: "tl",
    accent: null,
  },
  {
    id: "1y13AZVBVdi3UwMhDhaI",
    name: "Amihan",
    gender: "feminine",
    description: "Soft and calm Filipina voice, great for narration",
    language: "tl",
    accent: null,
  },
  // Turkish
  {
    id: "UtI8LSMMDNx2i47tnKLQ",
    name: "Marcus",
    gender: "masculine",
    description: "Friendly and natural Turkish male voice with a casual tone",
    language: "tr",
    accent: null,
  },
  {
    id: "aEJD8mYP0nuof1XHShVY",
    name: "Gozde",
    gender: "feminine",
    description: "Soft and warm Turkish female voice with a clear tone",
    language: "tr",
    accent: null,
  },
  // Ukrainian
  {
    id: "h9NSQvWZaC4NFusYsxT9",
    name: "Artem",
    gender: "masculine",
    description: "Rich and deep Ukrainian male voice, great for podcasts",
    language: "uk",
    accent: null,
  },
  {
    id: "bg0e02brzo3RVUEbuZeo",
    name: "Tonya",
    gender: "feminine",
    description: "Clear and direct Ukrainian female voice",
    language: "uk",
    accent: null,
  },
  // Vietnamese
  {
    id: "ueSxRO0nLF1bj93J2hVt",
    name: "Trung",
    gender: "masculine",
    description: "Clear and informative Vietnamese male voice",
    language: "vi",
    accent: null,
  },
  {
    id: "foH7s9fX31wFFH2yqrFa",
    name: "Huyen",
    gender: "feminine",
    description: "Calm and friendly Vietnamese female voice with a clear tone",
    language: "vi",
    accent: null,
  },
];

const ELEVEN_LABS_VOICES = _ELEVEN_LABS_VOICE_BASES.map((voice) => ({
  ...voice,
  provider: "elevenlabs" as const,
  modelId: "eleven_v4_turbo" as const,
}));

export const elevenLabsLanguageAndAccentToVoices = ({
  language,
  accent,
}: {
  language: ElevenLabsLanguage;
  accent: ElevenLabsAccent;
}): ElevenLabsVoice[] => {
  return ELEVEN_LABS_VOICES.filter(
    (voice) => voice.language === language && voice.accent === accent
  );
};

export const elevenLabsVoiceKey = (id: string, language: ElevenLabsLanguage) =>
  `${id}:${language}`;

export const VOICE_ID_TO_ELEVEN_LABS_VOICE = ELEVEN_LABS_VOICES.reduce(
  (acc, voice) => {
    acc[elevenLabsVoiceKey(voice.id, voice.language)] = voice;
    return acc;
  },
  {} as Record<string, ElevenLabsVoice>
);
